import { Popover as NextUIPopover } from '@nextui-org/react';
import styled from 'styled-components';

import { ChevronIcon } from '../../assets';
import ZIndexLayers from '../../services/ZIndexLayers/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import Button from '../Button';
import SecondaryButton from '../Button/SecondaryButton';
import Modal from '../Modal';
import { ShimmerText } from '../Shimmer/Shimmer.styles';

export const DropdownWrapper = styled.div<{ $labelPosition: 'top' | 'left' }>`
  display: flex;
  flex-direction: ${({ $labelPosition }) => ($labelPosition === 'left' ? 'row' : 'column')};
  align-items: ${({ $labelPosition }) => ($labelPosition === 'left' ? 'center' : 'flex-start')};
  gap: ${({ $labelPosition }) => ($labelPosition === 'left' ? '0.75rem' : '0.25rem')};
`;

export const DropdownLabel = styled.span`
  font-family: ${({ theme }: ThemeProp) => theme.fonts.primary};
  font-size: ${({ theme }: ThemeProp) => theme.fontVariants.desktop.body2.fontSize};
  font-style: normal;
  font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.regular};
  line-height: ${({ theme }: ThemeProp) => theme.lineHeight.s};
  color: ${({ theme }: ThemeProp) => theme.colors.content.subdued.high};
`;

export const TriggerButton = styled(Button)<{ $isOpen?: boolean }>`
  height: 2rem;
  width: 9.5rem;
  background: ${({ theme }: ThemeProp) => theme.colors.background};
  color: ${({ theme }: ThemeProp) => theme.colors.content.default};
  border: 1px solid ${({ theme }: ThemeProp) => theme.colors.neutrals.c300};
  border-radius: 0.5rem;

  &:hover,
  &:focus,
  &:active {
    background: ${({ theme }: ThemeProp) => theme.colors.background};
    height: 2rem;
  }
`;

export const IconTriggerButton = styled(Button)`
  min-width: 2.25rem;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border-radius: 0.5rem;
  background: ${(props: ThemeProp) => props.theme.colors.black};
  color: ${({ theme }: ThemeProp) => theme.colors.content.default};
  border: 1px solid ${({ theme }: ThemeProp) => theme.colors.neutrals.c300};

  &:hover,
  &:focus,
  &:active {
    background: ${({ theme }: ThemeProp) => theme.colors.neutrals.c050};
    border: 1px solid ${({ theme }: ThemeProp) => theme.colors.neutrals.c300};
    transform: none;
    opacity: 1;
  }
`;

export const TriggerContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 9.5rem;
  padding: 0.75rem;
`;

export const TriggerLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 7rem;
`;

export const TriggerChevronContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  flex-shrink: 0;
`;

export const Chevron = styled(ChevronIcon)<{ $isOpen: boolean }>`
  flex-shrink: 0;
  stroke: ${({ theme }: ThemeProp) => theme.colors.neutrals.c500};
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`;

export const ContentContainer = styled(NextUIPopover.Content)`
  min-width: 14rem;
  max-width: 16rem;
  padding: 0.5rem;
  border: 1px solid var(#e9eaeb);
  border-radius: 0.5rem;
  box-shadow: ${({ theme }: ThemeProp) => theme.shadows.tooltips};
`;

export const StyledModal = styled(Modal)`
  [data-rsbs-overlay] {
    border-radius: 1.5rem 1.5rem 0 0;
  }

  [data-rsbs-header] {
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }
`;

export const MobileContentContainer = styled.div`
  width: 100%;
`;

export const SearchContainer = styled.div`
  margin-bottom: 0.5rem;
  padding: 0.5rem;
`;

export const ItemsContainer = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isMobile }) => ($isMobile ? '0.5rem' : '0.25rem')};
  max-height: 14rem;
  overflow-y: auto;
  padding: ${({ $isMobile }) => ($isMobile ? '0.5rem 1rem' : '0')};
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
`;

export const ItemButton = styled.button<{ $isSelected?: boolean; $isMobile?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: ${({ $isMobile }) => ($isMobile ? '22.375rem' : '14rem')};
  border: none;
  border-radius: 0.375rem;
  text-align: left;
  cursor: pointer;
  padding: 0.5rem 0.625rem;
  background: ${({ theme, $isSelected }: ThemeProp & { $isSelected?: boolean }) =>
    $isSelected ? theme.colors.main.c050 : 'transparent'};

  .nextui-radio-label {
    margin-top: 0 !important;
  }

  &:hover {
    background: ${({ theme }: ThemeProp) => theme.colors.main.c050};
  }
`;

export const ItemTitle = styled.div<{ $isSelected?: boolean; $isMobile?: boolean }>`
  font-size: ${({ theme }: ThemeProp) => theme.fontVariants.desktop.body2.fontSize};
  font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.regular};
  font-family: ${({ theme }: ThemeProp) => theme.fonts.primary};
  color: ${({ theme, $isSelected }: ThemeProp & { $isSelected?: boolean }) =>
    $isSelected ? theme.colors.main.c600 : theme.colors.content.default};
`;

export const EmptyState = styled.div`
  padding: 0.5rem;
  color: ${({ theme }: ThemeProp) => theme.colors.content.subdued.high};
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.s};
`;

export const Actions = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  margin-top: ${({ $isMobile }) => ($isMobile ? '0' : '0.75rem')};
  padding: ${({ $isMobile }) => ($isMobile ? '1rem 1.25rem' : '0')};
  align-items: ${({ $isMobile }) => ($isMobile ? 'flex-start' : 'center')};
  gap: ${({ $isMobile }) => ($isMobile ? '0.75rem' : '0.5rem')};
  align-self: ${({ $isMobile }) => ($isMobile ? 'stretch' : 'center')};
  border-top: 1px solid #e9e9eb;
`; /* TODO: Change to theme color when we have it ---core-border-separator */

export const CancelButton = styled(SecondaryButton)`
  flex: 1;
  border-radius: 0.5rem;
  height: 2rem;
  padding: 0.375rem 1rem;
`;

export const ConfirmButton = styled(Button)`
  flex: 1;
  border-radius: 0.5rem;
  height: 2rem;
  padding: 0.375rem 1rem;
`;

export const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
`;

export const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

export const Description = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.s};
  color: ${({ theme }: ThemeProp) => theme.colors.content.subdued.high};
`;

export const IconSelectedIndicator = styled.div`
  display: flex;
  height: 1rem;
  min-width: 1rem;
  padding: 0.125rem;
  justify-content: center;
  align-items: center;
  gap: 0.125rem;
  position: absolute;
  right: -0.36238rem;
  top: -0.5rem;
  background-color: ${({ theme }: ThemeProp) => theme.colors.main.c600};
  color: ${({ theme }: ThemeProp) => theme.colors.black};
  border-radius: 62.4375rem;

  font-family: ${({ theme }: ThemeProp) => theme.fonts.primary};
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.xxs};
  font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.regular};
  z-index: ${ZIndexLayers.immediatelyAboveNavbar};
`;

export const TriggerButtonContainer = styled.div`
  position: relative;
`;

export const ShimmerButton = styled(ShimmerText).attrs(() => ({
  $height: '3rem',
  $borderRadius: '0.375rem',
}))`
  flex: 1;
`;
